import * as d3 from 'd3';
import { round } from 'lodash';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import MomentPropTypes from 'react-moment-proptypes';

export default class BellCurve extends Component {
  static gaussian = (x, mean, sigma) => {
    const gaussianConstant = 1 / Math.sqrt(2 * Math.PI);
    const standard = (x - mean) / sigma;
    return (gaussianConstant * Math.exp(-0.5 * standard * standard)) / sigma;
  };

  static toByteString = (byteAmount, roundToDecimal = 2) => {
    let byteValue = '';
    let unit = '';
    const amount = Number(byteAmount);
    if (amount < 1000) {
      byteValue = round(amount * 1, roundToDecimal);
      unit = 'B';
    } else if (amount >= 1000 && amount < 1000000) {
      byteValue = round(amount / 1000, roundToDecimal);
      unit = 'KB';
    } else if (amount >= 1000000 && amount < 1000000000) {
      byteValue = round(amount / 1000000, roundToDecimal);
      unit = 'MB';
    } else if (amount >= 1000000000) {
      byteValue = round(amount / 1000000000, roundToDecimal);
      unit = 'GB';
    }
    return `${byteValue} ${unit}`;
  };

  constructor(props) {
    super(props);
    this.chart = React.createRef();
  }

  componentDidMount() {
    this.buildChart();
  }

  getMean = () => {
    const { data } = this.props;
    const allValues = data.reduce((acc, curr) => acc + parseInt(curr.datausage, 10), 0);
    return allValues / data.length;
  };

  getSD = () => {
    const mean = this.getMean();
    const { data } = this.props;
    const sumOfSquareDifferences = data.reduce(
      (acc, curr) => acc + (parseInt(curr.datausage, 10) - mean) ** 2,
      0
    );

    return Math.sqrt(sumOfSquareDifferences / data.length);
  };

  getValue = (percent) => {
    const zScores = {
      10: -1.282,
      25: -0.68,
      75: 0.68,
      90: 1.282,
    };

    const percentileValue = this.getMean() + zScores[percent] * this.getSD();
    return percentileValue;
  };

  buildChart = () => {
    const { width, height, margin, lowPercent, highPercent, data } = this.props;
    const dataPoints = [];

    const mean = this.getMean();
    const std = this.getSD();
    const lowX = this.getValue(lowPercent);
    const highX = this.getValue(highPercent);
    const quartileNumbers = {
      lowTail: 0,
      lowMid: 0,
      highMid: 0,
      highTail: 0,
    };

    data.forEach((d) => {
      const dataUsage = parseInt(d.datausage, 10);
      if (dataUsage <= lowX) {
        quartileNumbers.lowTail += 1;
      }
      if (dataUsage > lowX && dataUsage <= mean) {
        quartileNumbers.lowMid += 1;
      }
      if (dataUsage > mean && dataUsage < highX) {
        quartileNumbers.highMid += 1;
      }
      if (dataUsage >= highX) {
        quartileNumbers.highTail += 1;
      }
    });

    const min = mean - std * 3;
    const max = mean + std * 3;
    const intervalCount = data.length - 1;
    const intervalValue = (max - min) / intervalCount;

    data.forEach((d, i) => {
      if (i === 0) return dataPoints.push({ q: min, p: BellCurve.gaussian(min, mean, std) });
      const value = dataPoints[i - 1].q + intervalValue;
      dataPoints.push({ q: value, p: BellCurve.gaussian(value, mean, std) });
      return undefined;
    });

    const chart = d3
      .select(this.chart.current)
      // svg canvas
      .append('svg')
      .attr('width', width + margin.left + margin.right)
      .attr('height', height + margin.top + margin.bottom)
      .style('background', '#ffffff')
      // group for axes
      .append('g')
      .attr('transform', `translate(${margin.left}, ${margin.top})`);

    // X axis ticks
    const xScale = d3
      .scaleLinear()
      .domain(d3.extent(dataPoints, (d) => d.q))
      .range([0, width]);

    const xAxis = d3.axisBottom(xScale);

    chart
      .append('g')
      .attr('transform', `translate(0, ${height})`)
      .call(xAxis.tickValues([]).tickSize(0));

    // Y axis ticks
    const yScale = d3
      .scaleLinear()
      .domain(d3.extent(dataPoints, (d) => d.p))
      .range([height, 0]);

    // lower percentile vertical marker
    chart
      .append('line')
      .attr('class', 'lower-cutoff')
      .attr('x1', xScale(lowX))
      .attr('y1', yScale(BellCurve.gaussian(lowX, mean, std)))
      .attr('x2', xScale(lowX))
      .attr('y2', height)
      .attr('stroke-width', 2)
      .style('stroke', '#099FC3');

    chart
      .append('line')
      .attr('class', 'lower-cutoff')
      .attr('x1', xScale(lowX))
      .attr('y1', yScale(BellCurve.gaussian(lowX, mean, std)))
      .attr('x2', xScale(lowX))
      .attr('y2', yScale(BellCurve.gaussian(lowX, mean, std)) - 40)
      .attr('stroke-width', 2)
      .style('stroke', '#099FC3')
      .style('opacity', '.2');

    // higher percentile vertical marker
    chart
      .append('line')
      .attr('class', 'higher-cutoff')
      .attr('x1', xScale(highX))
      .attr('y1', yScale(BellCurve.gaussian(highX, mean, std)))
      .attr('x2', xScale(highX))
      .attr('y2', height)
      .attr('stroke-width', 2)
      .style('stroke', '#FF54B3');

    chart
      .append('line')
      .attr('class', 'higher-cutoff')
      .attr('x1', xScale(highX))
      .attr('y1', yScale(BellCurve.gaussian(highX, mean, std)))
      .attr('x2', xScale(highX))
      .attr('y2', yScale(BellCurve.gaussian(highX, mean, std)) - 40)
      .attr('stroke-width', 2)
      .style('stroke', '#FF54B3')
      .style('opacity', '.2');

    // Create line curve
    const line = d3
      .line()
      .curve(d3.curveNatural)
      .x((d) => xScale(d.q))
      .y((d) => yScale(d.p));

    // colored gradient
    chart
      .append('path')
      .datum(dataPoints)
      .attr('class', 'line')
      .attr('d', line)
      .style('fill', 'url(#gradient)')
      .style('opacity', '10%')
      .attr('clip-path', 'url(#g-clip)');

    // Clip path for the gradient for curve
    chart
      .append('clipPath')
      .attr('id', 'grad-clip')
      .append('rect')
      .attr('class', 'g-clip')
      .attr('x', 0)
      .attr('y', 0)
      .attr('width', width)
      .attr('height', height);

    // mean vertical marker
    chart
      .append('line')
      .attr('class', 'mean-cutoff')
      .attr('x1', xScale(mean))
      .attr('y1', yScale(BellCurve.gaussian(mean, mean, std)))
      .attr('x2', xScale(mean))
      .attr('y2', height)
      .attr('stroke-width', 4)
      .style('stroke', '#ffffff');

    // Line gradient
    const gradient = chart
      .append('linearGradient')
      .attr('id', 'gradient')
      // top to bottom
      .attr('x2', '0%')
      .attr('y2', '100%');

    gradient
      .append('stop')
      .attr('class', 'stop-top')
      .attr('offset', '0')
      .style('stop-color', '#2F6AFF');

    gradient
      .append('stop')
      .attr('class', 'stop-bottom')
      .attr('offset', '1')
      .style('stop-color', '#FFFFFF');

    // Data join
    chart
      .append('path')
      .datum(dataPoints)
      .attr('class', 'line')
      .attr('d', line)
      .attr('fill', 'none')
      .attr('stroke', '#2F6AFF')
      .attr('stroke-width', 1.5);

    // Text stuff
    chart
      .append('text')
      .attr('id', 'lower-cutoff-text')
      .attr('x', xScale(lowX))
      .attr('dx', -40)
      .attr('y', yScale(BellCurve.gaussian(lowX, mean, std)))
      .attr('dy', -70)
      .text(`${lowPercent}th %tile`)
      .style('font-size', '14px');

    chart
      .append('text')
      .attr('id', 'lower-cutoff-text')
      .attr('x', xScale(lowX))
      .attr('dx', -35)
      .attr('y', yScale(BellCurve.gaussian(lowX, mean, std)))
      .attr('dy', -50)
      .text(`< ${BellCurve.toByteString(lowX)}`)
      .style('font-size', '11px')
      .style('fill', '#099FC3')
      .style('font-weight', 'normal');

    chart
      .append('text')
      .attr('id', 'higher-cutoff-text')
      .attr('x', xScale(highX))
      .attr('dx', -20)
      .attr('y', yScale(BellCurve.gaussian(highX, mean, std)))
      .attr('dy', -70)
      .text(`${highPercent}th %tile`)
      .style('font-size', '14px');

    chart
      .append('text')
      .attr('id', 'higher-cutoff-text')
      .attr('x', xScale(highX))
      .attr('dx', -15)
      .attr('y', yScale(BellCurve.gaussian(highX, mean, std)))
      .attr('dy', -50)
      .text(`${BellCurve.toByteString(highX)}`)
      .style('font-size', '11px')
      .style('fill', '#FF54B3');

    chart
      .append('text')
      .attr('id', 'mean-text')
      .attr('x', xScale(mean))
      .attr('dx', -30)
      .attr('y', yScale(BellCurve.gaussian(mean, mean, std)))
      .attr('dy', -10)
      .text(`${BellCurve.toByteString(mean)}`)
      .style('font-size', '14px')
      .style('fill', '#2F6AFF');

    // Device buckets
    chart
      .append('text')
      .attr('class', 'section-1')
      .attr('x', 0)
      // .attr('dx', 35)
      .attr('y', height)
      .attr('dy', 20)
      .text(`${quartileNumbers.lowTail} devices`)
      .style('font-size', '11px');

    chart
      .append('text')
      .attr('class', 'section-2')
      .attr('x', xScale(lowX))
      .attr('dx', 10)
      .attr('y', height)
      .attr('dy', 20)
      .text(`${quartileNumbers.lowMid} devices`)
      .style('font-size', '11px');

    chart
      .append('text')
      .attr('class', 'sections-3')
      .attr('x', xScale(mean))
      .attr('dx', 20)
      .attr('y', height)
      .attr('dy', 20)
      .text(`${quartileNumbers.highMid} devices`)
      .style('font-size', '11px');

    chart
      .append('text')
      .attr('class', 'sections-4')
      .attr('x', xScale(highX))
      .attr('dx', 70)
      .attr('y', height)
      .attr('dy', 20)
      .text(`${quartileNumbers.highTail} devices`)
      .style('font-size', '11px');
  };

  render() {
    return <div ref={this.chart} />;
  }
}

/**
 * lowPercent: number to indicate the lower percentile marker
 * highPercent: number to indicate the higher percentile marker
 * margin: object with top, right, bottom, and left keys
 *  to indicate the margins from the svg element
 * height: height in px of the data area
 *  (svg height is determined by adding height, margin.top and margin.bottom elements)
 * width: width in px of the data area
 *  (svg width is determined by adding width, margin.left and margin.right elements)
 * data: array of objects with dateofusage, numDevices, and datausage keys
 */

BellCurve.defaultProps = {
  lowPercent: 10,
  highPercent: 90,
  margin: { top: 50, right: 20, bottom: 50, left: 50 },
  height: 200,
  width: 500,
  data: [],
};

BellCurve.propTypes = {
  lowPercent: PropTypes.number,
  highPercent: PropTypes.number,
  margin: PropTypes.shape({
    top: PropTypes.number,
    right: PropTypes.number,
    bottom: PropTypes.number,
    left: PropTypes.number,
  }),
  height: PropTypes.number,
  width: PropTypes.number,
  data: PropTypes.arrayOf(
    PropTypes.shape({
      dateofusage: MomentPropTypes.momentObj,
    })
  ),
};
